import { FC, PropsWithChildren, useCallback, useEffect, useState } from "react";
import { useAppDataContext } from "@context/AppContext";
import {
  Avatar,
  Box,
  LinearProgress,
  Paper,
  Typography,
  TypographyOwnProps,
  useTheme,
} from "@mui/material";
import CurrentScore from "../CurrentScore";
import { useResponsiveContext } from "@context/ResponsiveContext";
import { gameState, useTeamSides } from "@/services/gameState";
import { useActionBookmarksContext } from "@context/ActionBookmarksContext";
import { Teams } from "@/models/gameState";

const RefereeTabletHeader: FC = () => {
  const { onMobile } = useResponsiveContext();
  const { addActionBookmark, removeActionBookmark, actionBookmarks } =
    useActionBookmarksContext();
  const { leftTeam, rightTeam } = useTeamSides(useAppDataContext());
  const teams = gameState("teams");
  const lastVisibleAction = gameState("lastVisibleAction");
  const connectionStatus = gameState("connectionStatus");

  const [isBookmarked, setIsBookmarked] = useState(false);

  useEffect(() => {
    setIsBookmarked(
      !!lastVisibleAction &&
        !!actionBookmarks?.find(
          (bookmark) => bookmark === lastVisibleAction.orderRank,
        ),
    );
  }, [lastVisibleAction, actionBookmarks]);

  const toggleBookmarkForLastAction = useCallback(
    (orderRank?: number) => {
      if (!orderRank) {
        return;
      }
      if (isBookmarked) {
        removeActionBookmark(orderRank);
      } else {
        addActionBookmark(orderRank);
      }
    },
    [isBookmarked, addActionBookmark, removeActionBookmark],
  );

  const TeamName: FC<
    PropsWithChildren<{ align: TypographyOwnProps["align"] }>
  > = ({ children, align }) => {
    return (
      <Typography
        align={align}
        fontSize={"5vw"}
        flex={1}
        overflow={"hidden"}
        whiteSpace={"nowrap"}
        textOverflow={"ellipsis"}
      >
        {children}
      </Typography>
    );
  };

  const JerseyColorStripe: FC<{ teams: Teams; side: "left" | "right" }> = ({
    teams,
    side,
  }) => {
    const { palette } = useTheme();
    const team = teams[side === "left" ? leftTeam : rightTeam];
    const hasJerseyInfo =
      team.selectedJersey && team.jerseys[team.selectedJersey];
    const borderStyle = `0.2vw solid ${palette.mode === "light" ? "black" : "#c6c6c6"}`;
    return hasJerseyInfo ? (
      <Box
        sx={{
          width: "100%",
          height: "100%",
          display: "flex",
          flexDirection: "column",
          gap: "0.1vw",
        }}
      >
        <Box
          sx={{
            boxSizing: "border-box",
            backgroundColor: team.jerseys[team.selectedJersey].shirtColorHex,
            width: "100%",
            height: "100%",
            borderRadius: side === "left" ? "0 0 2vw 0" : "0 0 0 2vw ",
            [`border${side === "left" ? "Right" : "Left"}`]: borderStyle,
            borderBottom: borderStyle,
          }}
        />
        <Box
          sx={{
            boxSizing: "border-box",
            backgroundColor: team.jerseys[team.selectedJersey].shortsColorHex,
            width: "100%",
            height: "100%",
            borderRadius: side === "left" ? "0 2vw  0 0" : "2vw 0 0 0",
            [`border${side === "left" ? "Right" : "Left"}`]: borderStyle,
            borderTop: borderStyle,
          }}
        />
      </Box>
    ) : (
      <Box />
    );
  };

  return (
    <Paper elevation={5} square sx={{ flexShrink: 0 }}>
      <Box sx={{ position: "absolute", top: 0, left: 0, right: 0 }}>
        {connectionStatus === "paused" && (
          <LinearProgress variant={"indeterminate"} />
        )}
      </Box>
      <Box
        display={"grid"}
        gridTemplateColumns={"2vw 1fr auto 1fr 2vw"}
        justifyContent={"center"}
        alignItems={"center"}
        gap={"2vw"}
        onClick={() =>
          toggleBookmarkForLastAction(lastVisibleAction?.orderRank)
        }
      >
        {leftTeam && rightTeam && (
          <>
            <JerseyColorStripe teams={teams} side={"left"} />
            {teams[leftTeam].logoImageMax400x400 ? (
              <Box
                display={"grid"}
                gridTemplateColumns={onMobile ? "1fr" : "12vw 1fr"}
                justifyItems={onMobile ? "center" : "unset"}
                alignItems={"center"}
              >
                <Avatar
                  src={teams[leftTeam].logoImageMax400x400.url}
                  sx={{
                    flex: "0 0 auto",
                    width: "12vw",
                    height: "12vw",
                  }}
                  alt={teams[rightTeam].clubCode}
                />
                {!onMobile && (
                  <TeamName align={"right"}>
                    {teams[leftTeam].clubCode}
                  </TeamName>
                )}
              </Box>
            ) : (
              <TeamName align={"right"}>{teams[leftTeam].name}</TeamName>
            )}
            <Box sx={{ py: "1vw" }}>
              <CurrentScore
                sx={{
                  flex: "0 0 auto",
                  fontWeight: "bold",
                }}
                fontSize={"8vw"}
                greyBackground={true}
              />
            </Box>
            {teams[rightTeam].logoImageMax400x400 ? (
              <Box
                display={"grid"}
                gridTemplateColumns={onMobile ? "1fr" : "1fr 12vw"}
                justifyItems={onMobile ? "center" : "unset"}
                alignItems={"center"}
              >
                {!onMobile && (
                  <TeamName align={"left"}>
                    {teams[rightTeam].clubCode}
                  </TeamName>
                )}
                <Avatar
                  src={teams[rightTeam].logoImageMax400x400.url}
                  sx={{
                    flex: "0 0 auto",
                    width: "12vw",
                    height: "12vw",
                  }}
                  alt={teams[rightTeam].clubCode}
                />
              </Box>
            ) : (
              <TeamName align={"right"}>{teams[rightTeam].name}</TeamName>
            )}
            <JerseyColorStripe teams={teams} side={"right"} />
          </>
        )}
      </Box>
    </Paper>
  );
};

export default RefereeTabletHeader;
